import React, { ReactElement } from 'react';

import { Divider, Pagination, Skeleton } from 'antd';
import { Row, Space, Text, Column } from 'farmhub-ui-core';

import { EntityWithAuth, SelectedEntityDomain } from 'api';
import { useAppSelector } from 'app';
import { EntityCardSkeleton, SlideSection, TitleText } from 'components';
import EntityTag from 'components/Commons/EntityTag';
import { selectAccount } from 'slice';
import { theme } from 'styled';
import { getDummyArray } from 'utils';

import AccountCard from '../AccountCardItem';
import {
  PaginationWrapper,
  Wrapper,
  WrapperBottom,
  WrapperItem,
  WrapperItemEmpty,
} from './styles';

interface MyEntitiesProps {
  total: number;
  pagination?: any;
  loading?: boolean;
  entityType: string;
  isFilterChange?: boolean;
  children: ReactElement[] | ReactElement;
  isLoadingOptions?: boolean;
  selectedOptionIDs: string[];
  currentList: EntityWithAuth[];
  renderEntityName: (e: string) => any;
  handleFilterByTag: (e: string) => void;
  handleChangeAccountList: (e: any) => void;
  handleSelectedAccount: (value: SelectedEntityDomain) => void;
  handleChangePagination?: (page: number, pageSize?: number) => void;
}

const MyEntitiesDesktop = ({
  total,
  children,
  currentList,
  entityType,
  pagination,
  loading = false,
  isFilterChange,
  selectedOptionIDs,
  isLoadingOptions,
  renderEntityName,
  handleFilterByTag,
  handleSelectedAccount,
  handleChangePagination,
  handleChangeAccountList,
}: MyEntitiesProps): JSX.Element => {
  const account = useAppSelector(selectAccount);
  const [isFirstLoading, setIsFirstLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    handleChangePagination && handleChangePagination(1);
  }, [isFilterChange]);

  const handleChangeSizePage = (current, pageSize): void => {
    handleChangePagination && handleChangePagination(current, pageSize);
  };

  React.useEffect(() => {
    setIsFirstLoading(false);
  }, []);
  return (
    <>
      <Column alignItems="center" styles={{ gap: 6 }}>
        <TitleText color="dark">Tài khoản của bạn</TitleText>
      </Column>
      <WrapperBottom id="detail_account">
        {isFirstLoading ? (
          <FilterSkeleton />
        ) : (
          <Row
            justifyContent="space-between"
            styles={{ padding: `0px ${theme.app.padding.vertical}` }}
          >
            {children}
          </Row>
        )}
        {isLoadingOptions ? (
          <TabFilterSkeleton />
        ) : (
          account?.option?.list?.length > 0 && (
            <Row width="100%">
              <SlideSection
                sectionProps={{
                  width: '100%',
                  wrapperPadding: '12px 16px',
                }}
                slideShowProps={{ spacing: 0 }}
              >
                {account?.option?.list?.length > 0 && (
                  <Row styles={{ 'flex-shrink': 0 }} width="fit-content">
                    <EntityTag
                      active={selectedOptionIDs.includes('all')}
                      borderRadius="6px"
                      height="22px"
                      margin={0}
                      name={
                        <Text
                          className="text"
                          size="xxs"
                          style={{
                            color: selectedOptionIDs.includes('all')
                              ? '#fff'
                              : '',
                          }}
                        >
                          Tất cả
                        </Text>
                      }
                      offset={[-10, 2]}
                      quantity={account?.option.total}
                      size="small"
                      onClick={() => handleFilterByTag('all')}
                    />
                  </Row>
                )}
                {account?.option?.list.map(item => (
                  <Row
                    key={item.id}
                    styles={{ 'flex-shrink': 0 }}
                    width="fit-content"
                  >
                    <EntityTag
                      active={selectedOptionIDs.includes(item.id)}
                      borderRadius="6px"
                      height="22px"
                      margin={0}
                      name={
                        <Text
                          className="text"
                          size="xxs"
                          style={{
                            color: selectedOptionIDs.includes(item.id)
                              ? '#fff'
                              : '',
                          }}
                        >
                          {item.content}
                        </Text>
                      }
                      offset={[-10, 2]}
                      quantity={item.count}
                      size="small"
                      onClick={() => handleFilterByTag(item.id)}
                    />
                  </Row>
                ))}
              </SlideSection>
            </Row>
          )
        )}
        <Divider style={{ margin: 0 }} />
        <Space height={12} />
        {loading ? (
          <WrapperItem>
            {getDummyArray(5).map(item => (
              <EntityCardSkeleton key={item.key} />
            ))}
          </WrapperItem>
        ) : currentList.length === 0 ? (
          <WrapperItemEmpty>
            {renderEntityName(entityType)?.icon}
            <Space height={24} />
            <Text>
              Hiện tại chưa có {renderEntityName(entityType)?.name} nào
            </Text>
            <Space height={24} />
          </WrapperItemEmpty>
        ) : (
          <WrapperItem>
            {currentList.map(item => (
              <AccountCard
                key={item.id}
                handleChangeAccountList={handleChangeAccountList}
                item={item}
                itemList={currentList}
                onSelected={handleSelectedAccount}
              />
            ))}
          </WrapperItem>
        )}
        <Space height={12} />
        <PaginationWrapper
          justifyContent="center"
          styles={{ textAlign: 'end' }}
        >
          <Pagination
            current={pagination?.current}
            pageSize={pagination.pageSize}
            showSizeChanger={false}
            total={total}
            onChange={handleChangePagination}
            onShowSizeChange={handleChangeSizePage}
          />
        </PaginationWrapper>
      </WrapperBottom>
    </>
  );
};

export default MyEntitiesDesktop;

const FilterSkeleton = (): JSX.Element => (
  <Row width="fit-content">
    <Wrapper styles={{ gap: 4 }}>
      <Skeleton.Input size="small" style={{ width: 206 }} />
      <Skeleton.Input size="small" style={{ width: 290 }} />
    </Wrapper>
  </Row>
);

const TabFilterSkeleton = (): JSX.Element => (
  <Row
    justifyContent="space-between"
    styles={{
      padding: ` ${theme.app.padding.horizontal}`,
    }}
  >
    <Skeleton.Input
      size="small"
      style={{ width: 70, height: 30, borderRadius: 12 }}
    />
    {getDummyArray(4).map(item => (
      <Skeleton.Input
        key={item.key}
        size="small"
        style={{ width: 100, height: 30, borderRadius: 12 }}
      />
    ))}
  </Row>
);
